























































































































































































import { Component, Prop, Mixins, Watch } from "vue-property-decorator";
import { mapState } from "vuex";
import { ValidationObserver } from "vee-validate/dist/types/components/Observer.d";

// Mixins
import RequestPromises from "@/mixins/requestPromises";
import LanguagesStore from "@/mixins/constants/languagesStore";
import HasValidation from "@/mixins/HasValidation";
import Context from "@/mixins/context";
import BrandsStore from "@/mixins/brandsStore";

// Enums
import { DataModules } from "@/store/modules/data";

// Types
import { IBrand } from "@/models/brands";
import { IClient, IClientForm } from "@/models/clients";
import { IState } from "@/store";
import { IPricelist } from "@/models/products";
import CurrenciesStore from "@/mixins/constants/currenciesStore";
import { IAccount } from "@/models/accounts";
import { UUID } from "@/data/enums";
import { ICurrency, ILanguage } from "@/models/constants";
import { SupportedLocaleCodes } from "@/data/enums/i18n";

@Component({
  components: {
    "custom-fields": () =>
      import("@/components/app/global/customFields/customFields.vue"),
    "notifications-disabled-checkbox": () =>
      import("@/components/app/global/fields/notificationsDisabledCheckbox.vue")
  },
  computed: {
    ...mapState({
      client(state: IState, getters) {
        return getters[`data/single`]({
          storeModule: DataModules.CLIENTS,
          scope: this.clientScope
        });
      },
      clientScope(state: IState, getters) {
        return getters[`data/${DataModules.CLIENTS}/scope`](this.clientId);
      },
      pricelists(state: IState, getters) {
        return getters[`data/list`]({
          storeModule: DataModules.PRICELISTS,
          scope: this.priceListsScope
        });
      },
      priceListsScope(state: IState, getters) {
        return getters[`data/${DataModules.PRICELISTS}/priceListsScope`]();
      },

      brandSettingsScope(state: IState, getters) {
        return getters[`data/${DataModules.BRANDS}/brandScope`](this.brandId);
      },
      brandSettingsData(state: IState, getters) {
        return (
          getters[`data/single`]({
            storeModule: DataModules.BRANDS,
            scope: this.brandSettingsScope
          }) || {}
        );
      }
    })
  }
})
export default class ClientProfileFields extends Mixins(
  BrandsStore,
  Context,
  CurrenciesStore,
  HasValidation,
  LanguagesStore,
  RequestPromises
) {
  public declare $refs: {
    observer: InstanceType<typeof ValidationObserver>;
    customFields: HTMLFormElement;
  };

  // Getters
  public client!: IClient;
  public clientScope!: string;
  public pricelists!: IPricelist[];
  public priceListsScope!: string;
  public brandSettingsData!: IBrand;
  public brandSettingsScope!: string;

  @Prop({ required: true })
  public clientId!: IClient["id"];

  @Prop({ default: true })
  public isCollapse!: boolean;

  @Prop({ default: false })
  public isDisabled!: boolean;

  @Prop({ default: true })
  public hasFormControls!: boolean;

  public customFieldsDataModule: string = DataModules.CUSTOM_FIELDS_CLIENTS;

  public form = {
    firstname: "",
    lastname: "",
    interface_language_id: "",
    custom_fields: {},
    notifications_disabled: false
  } as IClientForm;
  public formClone = {} as IClientForm;

  public isLoading = false;
  public isReloading = false;
  public requestError = false;
  public isProcessing = false;
  public sendingVerificationEmail = false;
  public isDeleteProcessing = false;
  public customFieldsLoaded = false;

  public async created() {
    if (!this.contentIsAvailable) return;

    this.isLoading = true;
    this.loadData();
  }

  public get isFormChanged(): boolean {
    return (
      this.customFieldsLoaded && !this.$_.isEqual(this.form, this.formClone)
    );
  }

  public revertToInitialState() {
    this.form = this.$_.cloneDeep(this.formClone);
  }

  public async loadData() {
    try {
      await this.getClientData(this.isReloading);

      if (this.brandId !== UUID.ORG && this.brandId !== this.client.brand_id) {
        this.$buefy.toast.open({
          message: this.$t("_.client_from_different_brand") as string
        });
        return this.$router.push({
          name: "adminClients"
        });
      }

      await this.getBrandSettings();

      if (this.isAdmin) {
        await this.getPriceLists();
      }

      this.refreshClientForm();
      this.requestError = false;
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
      this.requestError = true;
    } finally {
      this.isReloading = false;
      this.isLoading = false;
    }
  }

  public reloadData() {
    this.isReloading = true;
    this.loadData();
  }

  public get accountId(): IAccount["id"] {
    return this.$_.get(this.client, "accounts[0].id", "");
  }

  public get pricelistId() {
    return this.$_.get(this.client, "accounts[0].pricelist_id", "");
  }

  public get selectedPriceList() {
    return this.pricelists?.[this.pricelistId];
  }

  public get currencyId(): ICurrency["id"] {
    return this.$_.get(this.client, "accounts[0].currency_id", "");
  }

  public langDoesNotExist(langId: ILanguage["id"]) {
    return !this.$_.some(this.brandLanguages, ["id", langId]);
  }

  public getPriceLists() {
    return this.$store.dispatch(`data/${DataModules.PRICELISTS}/list`, {
      params: {
        limit: 0,
        order: "-created_at"
      },
      vm: this
    });
  }

  public getClientData(ignoreStored = false) {
    return this.$store.dispatch(
      `data/${DataModules.CLIENTS}/getClientWithRelatedData`,
      {
        id: this.clientId,
        scope: this.clientScope,
        ignoreStored,
        vm: this
      }
    );
  }

  public async getBrandSettings(ignoreStored = false) {
    if (this.brandId === UUID.ORG) return Promise.resolve();
    return this.$store.dispatch(`data/${DataModules.BRANDS}/getBrandSettings`, {
      id: this.$_.get(this.client, "brand_id"),
      scope: this.brandSettingsScope,
      params: {
        with: "languages"
      },
      vm: this,
      ignoreStored
    });
  }

  public get brandCurrencies() {
    return this.brandId !== UUID.ORG
      ? this.$_.get(this.$store, "state.brand.currencies", [])
      : this.$_.get(this.brandSettingsData, "currencies");
  }

  public get brandLanguages() {
    return this.brandId !== UUID.ORG
      ? this.$_.get(this.$store, "state.brand.languages", [])
      : this.$_.get(this.brandSettingsData, "languages");
  }

  public async resendVerification() {
    if (!this.$userCan("update_client")) {
      return;
    }
    this.sendingVerificationEmail = true;

    try {
      await this.$store.dispatch(
        `data/${DataModules.CLIENTS}/resendVerificationEmail`,
        { id: this.client.id }
      );

      this.$buefy.toast.open({
        message: this.$t("_.verification_email_sent") as string
      });
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
    }

    this.sendingVerificationEmail = false;
  }

  public findLanguageName(languageId: ILanguage["id"]) {
    return this.$_.get(
      this.$_.find(this.languages, langObj => {
        return langObj.id === languageId;
      }),
      "language",
      ""
    );
  }

  public get fieldIsDisabled() {
    if (this.isDisabled) return true;
    if (this.isProcessing) return true;
    return !this.$userCan("update_client");
  }

  public get formIsDisabled() {
    return (
      this.isDisabled ||
      this.isProcessing ||
      this.isDeleteProcessing ||
      !this.isFormChanged ||
      this.hasErrors
    );
  }

  public getLanguageCode(languageId: ILanguage["id"]) {
    return this.$_.get(
      this.$_.find(this.languages, language => {
        return language.id === languageId;
      }),
      "code",
      "en"
    );
  }

  public get clientIsNotVerified() {
    return (
      this.$_.get(this.client, "has_login") &&
      !this.$_.get(this.client, "verified", true)
    );
  }

  public async validateBeforeSubmit() {
    if (!this.canUpdateClient || this.isProcessing) {
      return;
    }
    this.isProcessing = true;

    try {
      if (!(await this.validate())) {
        throw this.$t("_sentence.form_is_not_valid");
      }

      await this.$refs["customFields"].uploadCustomImages();
      await this.updateClient();

      await this.getClientData(true);

      if (this.isClient) {
        if (
          this.form.interface_language_id !==
          this.formClone.interface_language_id
        ) {
          await this.$store.dispatch(
            "i18n/setLanguage",
            this.getLanguageCode(
              this.form.interface_language_id as SupportedLocaleCodes
            )
          );
        }
        await this.$store.dispatch("auth/client/getUser");
      }

      this.refreshClientForm();

      this.$store.dispatch("toast/show", {
        message: this.$t("_.object_successfully_updated", {
          object: this.$t("_.details")
        })
      });
    } catch (error) {
      this.form = this.$_.cloneDeep(this.formClone);
      this.$store.dispatch("api/handleValidationError", {
        error,
        vm: this
      });
    }

    this.isProcessing = false;
  }

  public refreshClientForm() {
    this.form = this.$_.merge(
      {},
      this.form,
      this.$_.pick(this.client, [
        "firstname",
        "lastname",
        "interface_language_id",
        "email",
        "notifications_disabled"
      ])
    );

    if (this.isAdmin) {
      this.form.pricelist_id = this.pricelistId;
    }
    this.form.currency_id = this.currencyId;

    this.$_.forEach(this.$_.get(this.client, "custom_fields", []), fieldObj => {
      this.form.custom_fields[fieldObj.field.code] = fieldObj.value;
    });

    this.form = this.$_.cloneDeep(this.form);
    this.formClone = this.$_.cloneDeep(this.form);
  }

  public async updateClient() {
    return Promise.all([
      this.updateClientProfile(),
      this.updateClientAccount(),
      this.updatePricelist()
    ]);
  }

  public copyToCloneForm() {
    this.formClone = this.$_.cloneDeep(this.form);
    this.customFieldsLoaded = true;
  }

  public updateClientProfile() {
    const differences = this.getDifferences(this.form, this.formClone);
    delete differences.pricelist_id;
    delete differences.currency_id;

    if (this.$_.isEmpty(differences.custom_fields)) {
      delete differences.custom_fields;
    } else {
      differences.custom_fields = this.$_.mapValues(
        differences.custom_fields,
        fieldValue => (fieldValue === "" ? null : fieldValue)
      );
    }

    if (this.$_.isEmpty(differences)) {
      return;
    }
    return this.$store.dispatch(`data/${DataModules.CLIENTS}/update`, {
      id: this.clientId,
      data: this.$_.merge(
        {},
        differences,
        this.form.interface_language_id !== this.client.interface_language_id
          ? { document_language_id: this.form.interface_language_id }
          : {}
      )
    });
  }

  public updatePricelist() {
    if (!this.accountId) return;
    if (this.form.pricelist_id === this.formClone.pricelist_id) return;
    return this.$store.dispatch(
      `data/${DataModules.CLIENTS_ACCOUNTS}/assignPricelist`,
      {
        accountId: this.accountId,
        data: {
          pricelist_id: this.form.pricelist_id
        }
      }
    );
  }

  public updateClientAccount() {
    if (
      this.form.currency_id === this.formClone.currency_id ||
      !this.accountId
    ) {
      return;
    }
    return this.$store.dispatch(`data/${DataModules.CLIENTS_ACCOUNTS}/update`, {
      accountId: this.accountId,
      data: {
        currency_id: this.form.currency_id
      }
    });
  }

  public getDifferences(firstObj, secondObj) {
    const differences: any = this.$_.omitBy(firstObj, (v, k) => {
      return secondObj[k] === v;
    });

    if (
      this.$_.find(differences, (difference, key) => {
        return key === "custom_fields";
      })
    ) {
      differences.custom_fields = this.getDifferences(
        differences.custom_fields,
        secondObj.custom_fields
      );
    }

    return differences;
  }

  public get contentIsAvailable() {
    return (
      this.isClient ||
      this.isGuest ||
      (this.isAdmin &&
        this.$userCan("get_client") &&
        this.$userCan("list_pricelists"))
    );
  }

  public get canUpdateClient() {
    return this.isClient || (this.isAdmin && this.$userCan("update_client"));
  }

  public get supportedCurrencies() {
    if (
      this.$_.some(
        this.brandCurrencies,
        currency => currency.id === this.form.currency_id
      )
    ) {
      return this.brandCurrencies;
    }
    const currency = this.$_.find(
      this.currencies,
      currency => currency.id === this.currencyId
    );

    return this.$_.orderBy(
      this.$_.concat(this.brandCurrencies, [currency]),
      "name",
      "asc"
    );
  }

  @Watch("isFormChanged")
  public isFormChangedUpdated() {
    return this.$emit("update:isFormChanged", this.isFormChanged);
  }

  @Watch("formIsDisabled")
  public formIsDisabledUpdated() {
    return this.$emit("update:formIsDisabled", this.formIsDisabled);
  }

  @Watch("isProcessing")
  public isProcessingUpdated() {
    return this.$emit("update:isProcessing", this.isProcessing);
  }

  public beforeDestroy() {
    this.$store.dispatch("data/bin", {
      storeModule: DataModules.CLIENTS,
      scope: this.clientScope,
      vm: this
    });

    this.$store.dispatch("data/binList", {
      storeModule: DataModules.PRICELISTS,
      vm: this
    });

    if (this.brandId === UUID.ORG) {
      this.$store.dispatch("data/binSingle", {
        storeModule: DataModules.BRANDS,
        scope: this.brandSettingsScope,
        vm: this
      });
    }
  }
}
